import { useId, useState } from 'react';
import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  ListSubheader,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
} from '@mui/material';
import { useTranslate } from 'react-admin';

interface EnumOption {
  value: string;
  label: string;
}

interface EnumMultiSelectProps {
  label: string;
  options: EnumOption[];
  values: string[];
  onChange: (values: string[]) => void;
}

/**
 * Option-count threshold above which the dropdown grows a type-to-filter
 * field. Below it, every option is visible at once and a filter box would
 * only push the options further from the pointer.
 */
const FILTER_THRESHOLD = 5;

function matches(option: EnumOption, input: string): boolean {
  const haystack = `${option.label} ${option.value}`.toLowerCase();
  return input
    .split(/\s+/)
    .filter((term) => term.length > 0)
    .every((term) => haystack.includes(term));
}

interface FilterRowProps {
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
}

/**
 * The type-to-filter row pinned at the top of the dropdown. Its own component
 * on purpose: MUI's `Select` clones every child injecting option props
 * (`role="option"`, `aria-selected`, click/keyboard handlers meant for
 * pickable values) — destructuring only this row's own props swallows that
 * injection, so the row stays a presentation tool, never an aria option.
 */
function EnumMultiSelectFilterRow({ value, placeholder, onChange }: FilterRowProps) {
  return (
    <ListSubheader role="presentation" sx={{ pt: 1, pb: 0.5, lineHeight: 'normal' }}>
      <TextField
        size="small"
        fullWidth
        autoFocus
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        // The Select's own keyboard handling (first-letter jump, Enter to
        // pick) must not swallow typing meant for this field.
        onKeyDown={(event) => event.stopPropagation()}
      />
    </ListSubheader>
  );
}
// Keyboard-highlight bookkeeping in MUI's Select skips this row like it skips
// a plain ListSubheader.
(EnumMultiSelectFilterRow as unknown as { muiSkipListHighlight: boolean }).muiSkipListHighlight = true;

/**
 * Multi-select filter for a query parameter whose values come from the
 * contract's own closed enum (`gateway`, `status`, `type`, audit actions…) —
 * a fixed list of checkboxes, never free text. Long lists (over
 * {@link FILTER_THRESHOLD} options) get a type-to-filter field pinned at the
 * top of the dropdown; the checkbox multi-select semantics are unchanged
 * either way.
 */
export function EnumMultiSelect({ label, options, values, onChange }: EnumMultiSelectProps) {
  const translate = useTranslate();
  const labelId = useId();
  const [filter, setFilter] = useState('');

  const filterable = options.length > FILTER_THRESHOLD;
  const input = filter.trim().toLowerCase();
  const shownOptions = filterable && input ? options.filter((option) => matches(option, input)) : options;

  return (
    <FormControl size="small" fullWidth>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        labelId={labelId}
        multiple
        value={values}
        onChange={(event) => {
          const next = event.target.value;
          onChange(typeof next === 'string' ? next.split(',') : next);
        }}
        onClose={() => setFilter('')}
        MenuProps={{ autoFocus: !filterable }}
        input={<OutlinedInput label={label} />}
        renderValue={(selected) =>
          selected
            .map((value) => options.find((option) => option.value === value)?.label ?? value)
            .join(', ')
        }
      >
        {filterable && (
          <EnumMultiSelectFilterRow
            placeholder={translate('pay.filterableSelect.filterPlaceholder')}
            value={filter}
            onChange={setFilter}
          />
        )}
        {shownOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            <Checkbox checked={values.includes(option.value)} size="small" />
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
